import { useMemo, useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  DAY_KEYS,
  useGeneratePlan,
  type CoachPlan,
  type CoachPlanRequest,
  type CoachPlanResponse,
  type DayKey,
  type Discipline,
  type PlannedWorkout,
} from '@/lib/coach';

/**
 * Visualización del plan generado por el coach.
 *
 * Soporta dos modos:
 * - Preview (no persistido): banner azul + botones "Aceptar plan" /
 *   "Descartar". "Aceptar" reenvía la MISMA petición con `preview: false`.
 * - Persistido: banner verde + link a la home (que, cuando exista vista
 *   semanal en M1.6, será el calendario).
 *
 * Decisiones de layout:
 * - Agrupación por día (Lun-Dom) en cards verticales: es lo que mejor se lee
 *   en mobile y en desktop por igual; una tabla 7-columnas obligaría a hacer
 *   scroll horizontal con descripciones largas.
 * - Markdown de `rationale`: solo soportamos párrafos separados por `\n\n`.
 *   No incrustamos `marked`/`react-markdown` para evitar añadir dependencia
 *   nueva y porque el prompt del backend ya pide texto plano.
 */
export function PlanPreview({
  result,
  request,
  onAccepted,
  onDiscard,
}: {
  result: CoachPlanResponse;
  request: CoachPlanRequest;
  onAccepted: (next: CoachPlanResponse) => void;
  onDiscard: () => void;
}): ReactElement {
  const acceptMutation = useGeneratePlan();

  function handleAccept() {
    if (acceptMutation.isPending) return;
    acceptMutation.mutate(
      { ...request, preview: false },
      {
        onSuccess: (next) => onAccepted(next),
      },
    );
  }

  return (
    <section aria-labelledby="plan-title" className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 id="plan-title" className="text-2xl font-semibold tracking-tight text-slate-900">
            Plan semanal
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {formatRange(result.plan.weekStartISO, result.plan.weekEndISO)}
          </p>
        </div>
      </header>

      {!result.persisted ? (
        <PreviewBanner
          onAccept={handleAccept}
          onDiscard={onDiscard}
          isPending={acceptMutation.isPending}
          error={acceptMutation.error}
        />
      ) : (
        <PersistedBanner />
      )}

      <PlanSummaryCard plan={result.plan} />

      <RationalePanel rationale={result.plan.rationale} />

      <WeekList workouts={result.plan.workouts} />

      <footer className="border-t border-slate-200 pt-3 text-xs text-slate-500">
        <span>
          Modelo: <code className="rounded bg-slate-100 px-1 py-0.5">{result.modelUsed}</code>
          {' · '}
          Tokens: {result.tokensUsed.toLocaleString('es-ES')}
          {' · '}
          Latencia: {(result.latencyMs / 1000).toFixed(1)}s
        </span>
      </footer>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

function PreviewBanner({
  onAccept,
  onDiscard,
  isPending,
  error,
}: {
  onAccept: () => void;
  onDiscard: () => void;
  isPending: boolean;
  error: Error | null;
}): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900"
    >
      <p className="font-medium">
        Esta es una previsualización. Pulsa &quot;Aceptar plan&quot; para guardarlo.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onAccept}
          disabled={isPending}
          aria-busy={isPending}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isPending ? 'Guardando…' : 'Aceptar plan'}
        </button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={isPending}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Descartar
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-3 rounded-md bg-rose-100 px-3 py-2 text-sm text-rose-800">
          No se pudo guardar el plan. {error.message ?? ''}
        </p>
      )}
    </div>
  );
}

function PersistedBanner(): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
    >
      <p className="font-medium">Plan guardado en tu calendario.</p>
      <Link
        to="/"
        className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2"
      >
        Ver en calendario
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

function PlanSummaryCard({ plan }: { plan: CoachPlan }): ReactElement {
  const { totalHours, byDiscipline } = plan.summary;
  const entries = (Object.entries(byDiscipline) as Array<[Discipline, number | undefined]>).filter(
    ([, v]) => typeof v === 'number' && v > 0,
  );
  return (
    <section
      aria-label="Resumen del plan"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Resumen
        </h2>
        <p className="text-sm">
          Total:{' '}
          <strong className="text-slate-900">{formatHours(totalHours)}</strong>
        </p>
      </div>
      {entries.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {entries.map(([disc, hours]) => (
            <li key={disc}>
              <span
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${disciplineBadgeClass(
                  disc,
                )}`}
              >
                <span aria-hidden="true">{disciplineIcon(disc)}</span>
                <span>{disciplineLabel(disc)}</span>
                <span className="font-semibold">{formatHours(hours ?? 0)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Rationale
// ---------------------------------------------------------------------------

function RationalePanel({ rationale }: { rationale: string }): ReactElement | null {
  const paragraphs = useMemo(() => splitParagraphs(rationale), [rationale]);
  if (paragraphs.length === 0) return null;
  return (
    <section
      aria-label="Razonamiento del coach"
      className="rounded-lg border border-teal-200 bg-teal-50/60 p-4"
    >
      <h2 className="text-sm font-semibold uppercase tracking-wide text-teal-700">
        Razonamiento del coach
      </h2>
      <div className="mt-2 space-y-2 text-sm text-slate-800">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </section>
  );
}

function splitParagraphs(s: string): string[] {
  return s
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

// ---------------------------------------------------------------------------
// Lista semanal
// ---------------------------------------------------------------------------

function WeekList({ workouts }: { workouts: PlannedWorkout[] }): ReactElement {
  const grouped = useMemo(() => groupByLocalDay(workouts), [workouts]);
  return (
    <section aria-label="Workouts de la semana" className="space-y-3">
      {DAY_KEYS.map((day) => {
        const items = grouped[day] ?? [];
        return (
          <article
            key={day}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
          >
            <header className="flex items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                {fullDayLabel(day)}
              </h3>
              <span className="text-xs text-slate-500">
                {items.length === 0
                  ? 'Descanso'
                  : `${items.length} sesión${items.length === 1 ? '' : 'es'}`}
              </span>
            </header>
            {items.length > 0 && (
              <ul className="mt-3 space-y-3">
                {items.map((w) => (
                  <WorkoutItem key={w.id} workout={w} />
                ))}
              </ul>
            )}
          </article>
        );
      })}
    </section>
  );
}

function WorkoutItem({ workout }: { workout: PlannedWorkout }): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const desc = workout.description ?? '';
  const isLong = desc.length > 140;
  const shown = !expanded && isLong ? `${desc.slice(0, 140).trimEnd()}…` : desc;

  return (
    <li className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-slate-900">
          {formatTime(workout.datetime, workout.timezone)}
        </span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${disciplineBadgeClass(
            workout.discipline,
          )}`}
        >
          <span aria-hidden="true">{disciplineIcon(workout.discipline)}</span>
          {disciplineLabel(workout.discipline)}
        </span>
        <span className="rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-700">
          {Math.round(workout.durationPlannedMin)} min
        </span>
        {workout.intensityZone && (
          <span className="rounded bg-slate-900 px-2 py-0.5 text-xs font-medium text-slate-50">
            {workout.intensityZone}
          </span>
        )}
      </div>
      {desc && (
        <div className="mt-2 text-sm text-slate-700">
          <p className="whitespace-pre-wrap">{shown}</p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-xs font-medium text-teal-700 hover:text-teal-800 focus:outline-none focus:underline"
            >
              {expanded ? 'Ver menos' : 'Ver más'}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers de display
// ---------------------------------------------------------------------------

function groupByLocalDay(workouts: PlannedWorkout[]): Record<DayKey, PlannedWorkout[]> {
  const empty: Record<DayKey, PlannedWorkout[]> = {
    mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [],
  };
  for (const w of workouts) {
    const dk = dayKeyFor(w.datetime, w.timezone);
    if (dk) empty[dk].push(w);
  }
  for (const k of DAY_KEYS) {
    empty[k].sort((a, b) => Date.parse(a.datetime) - Date.parse(b.datetime));
  }
  return empty;
}

/**
 * Devuelve la `DayKey` del workout en su timezone. Usamos
 * `Intl.DateTimeFormat` para extraer el día de la semana sin recurrir a libs.
 */
function dayKeyFor(iso: string, tz: string): DayKey | null {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      weekday: 'short',
    });
    const wd = fmt.format(new Date(iso)).toLowerCase();
    // wd ∈ {'mon','tue','wed','thu','fri','sat','sun'}
    if ((DAY_KEYS as readonly string[]).includes(wd)) return wd as DayKey;
    return null;
  } catch {
    return null;
  }
}

function formatTime(iso: string, tz: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatRange(startISO: string, endISO: string): string {
  try {
    const fmt = new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `${fmt.format(new Date(startISO))} — ${fmt.format(new Date(endISO))}`;
  } catch {
    return `${startISO} — ${endISO}`;
  }
}

function formatHours(h: number): string {
  if (Number.isInteger(h)) return `${h} h`;
  return `${h.toFixed(1)} h`;
}

function fullDayLabel(d: DayKey): string {
  switch (d) {
    case 'mon': return 'Lunes';
    case 'tue': return 'Martes';
    case 'wed': return 'Miércoles';
    case 'thu': return 'Jueves';
    case 'fri': return 'Viernes';
    case 'sat': return 'Sábado';
    case 'sun': return 'Domingo';
  }
}

function disciplineLabel(d: Discipline): string {
  switch (d) {
    case 'swim': return 'Natación';
    case 'bike': return 'Ciclismo';
    case 'run': return 'Carrera';
    case 'strength': return 'Fuerza';
    case 'brick': return 'Brick';
    case 'other': return 'Otro';
  }
}

function disciplineIcon(d: Discipline): string {
  switch (d) {
    case 'swim': return '~';
    case 'bike': return '>';
    case 'run': return '^';
    case 'strength': return '#';
    case 'brick': return '+';
    case 'other': return '·';
  }
}

/**
 * Clases Tailwind con suficiente contraste AA (texto sobre fondo). Cada
 * disciplina tiene un color distintivo según el brief: cyan/swim,
 * naranja/bike, verde/run, slate/strength, morado/brick.
 */
function disciplineBadgeClass(d: Discipline): string {
  switch (d) {
    case 'swim': return 'bg-cyan-100 text-cyan-900 ring-1 ring-cyan-200';
    case 'bike': return 'bg-orange-100 text-orange-900 ring-1 ring-orange-200';
    case 'run': return 'bg-emerald-100 text-emerald-900 ring-1 ring-emerald-200';
    case 'strength': return 'bg-slate-200 text-slate-900 ring-1 ring-slate-300';
    case 'brick': return 'bg-purple-100 text-purple-900 ring-1 ring-purple-200';
    case 'other': return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200';
  }
}
